import React from "react";
import { Button } from "@heroui/react";
import { useNavigate, useRouteError } from "react-router-dom";
import logo from "../assets/img/MASH-Primary-logo-Black-head-only-cool.png";
import * as Sentry from "@sentry/react";

export type ErrorType =
  | "NO_MARKETS"
  | "MARKET_NOT_FOUND"
  | "NO_ACTIVE_MARKETS"
  | "UNEXPECTED"
  | "PAGE_NOT_FOUND";

interface ErrorDisplayProps {
  type: ErrorType;
  error?: Error;
  resetErrorBoundary?: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  type,
  error,
  resetErrorBoundary,
}) => {
  const navigate = useNavigate();
  const routeError = useRouteError() as Error;
  // console.error(routeError.message);

  React.useEffect(() => {
    Sentry.captureException(routeError || error);
    console.log("Error was sent to sentry");
  }, [error]);

  const getErrorContent = () => {
    switch (type) {
      case "NO_MARKETS":
        return {
          title: "No Active Markets",
          message: "There are currently no active markets available.",
          action: "Try Again",
        };
      case "MARKET_NOT_FOUND":
        return {
          title: "Market Not Found",
          message:
            "The market you're looking for doesn't exist or has been closed.",
          action: "Go to Mash Home",
        };
      case "NO_ACTIVE_MARKETS":
        return {
          title: "No Active Markets",
          message: "There are currently no active markets available.",
          action: "Go to Mash Home",
        };
      case "UNEXPECTED":
        return {
          title: "Unexpected Error",
          message: "Something went wrong. Please try again later.",
          action: "Reload Page",
        };
      case "PAGE_NOT_FOUND":
        return {
          title: "Page Not Found",
          message: "The page you're looking for doesn't exist.",
          action: "Go to Home",
        };
    }
  };

  const content = getErrorContent();

  const handleAction = () => {
    // specific action for each error type
    if (type === "MARKET_NOT_FOUND") {
      navigate("/");
    } else if (type === "PAGE_NOT_FOUND" || type === "NO_MARKETS") {
      navigate("/");
    } else {
      resetErrorBoundary?.();
    }
  };

  const shouldShowErrorDetails = type === "UNEXPECTED" && error;

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden transition-opacity duration-1000">
      <div
        className={`absolute inset-0 bg-radial-gradient from-gray-600/20 via-gray-400/10 to-transparent animate-pulse`}
      />
      <div
        className={`absolute inset-0 bg-radial-gradient from-gray-600/15 via-gray-400/5 to-transparent animate-pulse delay-100`}
      />
      <div className="absolute inset-0 bg-radial-gradient from-transparent via-background/60 to-background" />
      <div className="relative z-10 flex flex-col items-center">
        <img
          src={logo}
          alt=""
          className={`w-32 h-auto animate-pulse opacity-30 transition-opacity duration-500`}
        />
        <h1 className="text-gray-400 mt-8 text-2xl font-semibold transition-opacity duration-500">
          {content.title}
        </h1>
        <p className="text-gray-500 mt-2 transition-opacity duration-500">
          {content.message}
        </p>
        {shouldShowErrorDetails && (
          <pre className="mt-4 p-4 bg-gray-800 rounded-lg text-sm text-red-500 max-w-lg overflow-auto">
            {error.message}
          </pre>
        )}
        <Button color="primary" className="mt-8" onClick={handleAction}>
          {content.action}
        </Button>
      </div>
    </div>
  );
};

export default ErrorDisplay;
